// Static demo portfolios shown to non-authenticated visitors.
//
// Each holding has a flexible activity model — you can mix a monthly DCA plan
// with explicit lump-sum buys and strategic sells:
//
//   ticker          — clean symbol shown in the UI
//   yahoo           — symbol used for /api/stock/history (may carry a suffix)
//   startDate       — (optional) first monthly DCA buy
//   sharesPerBuy    — (optional) shares bought each month while DCA runs
//   dcaCutoffPrice  — (optional) skip a monthly DCA buy when price ≥ cutoff
//                     (e.g. don't keep buying BTC above $90k)
//   actions         — (optional) explicit one-off buys/sells:
//                       [{ date, type: 'buy'|'sell', qty: number|'all' }]
//                     'all' on a sell liquidates the entire current position.
//
// Quantities, average cost and the chart curve are all derived live at render
// time from Yahoo history — so the demo always reflects current market prices.

export const DEMO_PORTFOLIOS = [
  {
    id: 'demo-usd',
    name: 'My Investment Portfolio',
    currency: 'USD',
    // Approximate lifetime realised P/L from the strategic sells (TSLA, AMD,
    // PLTR, SMCI, APP, CELH, KO/JNJ trims). Used by the Portfolios list cards;
    // the detail page computes the exact figure from live Yahoo history.
    realizedApprox: 145000,
    holdings: [
      // ── Growth core — DCA, with a cap so we don't keep paying top dollar ──
      { ticker: 'NVDA', yahoo: 'NVDA', name: 'NVIDIA Corp.',         assetType: 'Stock', startDate: '2020-05-15', sharesPerBuy: 2.5,  dividendYield: 0.03, dcaCutoffPrice: 130,
        // Trim ~30% of the position right at the November 2024 spike,
        // locking in gains and smoothing the chart's vertical peak.
        actions: [{ date: '2024-11-15', type: 'sell', qty: 60 }] },
      { ticker: 'AAPL', yahoo: 'AAPL', name: 'Apple Inc.',           assetType: 'Stock', startDate: '2020-05-15', sharesPerBuy: 0.45, dividendYield: 0.5 },
      { ticker: 'MSFT', yahoo: 'MSFT', name: 'Microsoft Corp.',      assetType: 'Stock', startDate: '2020-05-15', sharesPerBuy: 0.22, dividendYield: 0.7 },
      { ticker: 'AMZN', yahoo: 'AMZN', name: 'Amazon.com Inc.',      assetType: 'Stock', startDate: '2020-08-15', sharesPerBuy: 0.35, dividendYield: 0 },
      { ticker: 'GOOGL',yahoo: 'GOOGL',name: 'Alphabet Inc.',        assetType: 'Stock', startDate: '2021-01-15', sharesPerBuy: 0.30, dividendYield: 0.4 },
      { ticker: 'COST', yahoo: 'COST', name: 'Costco Wholesale',     assetType: 'Stock', startDate: '2020-08-15', sharesPerBuy: 0.06, dividendYield: 0.5 },

      // ── Cyclical / momentum trades — full sells at the peaks, free capital
      //    that the rest of the portfolio quietly redeploys. ──
      { ticker: 'TSLA', yahoo: 'TSLA', name: 'Tesla Inc.',           assetType: 'Stock', dividendYield: 0,
        actions: [
          { date: '2020-07-15', type: 'buy',  qty: 40 },     // pre-split entry
          { date: '2021-10-15', type: 'sell', qty: 'all' },  // ride into the late-2021 high
          { date: '2023-05-15', type: 'buy',  qty: 50 },     // mid-cycle re-entry around $170
          { date: '2024-11-15', type: 'sell', qty: 'all' },  // post-election rally exit
        ],
      },
      { ticker: 'PLTR', yahoo: 'PLTR', name: 'Palantir Technologies', assetType: 'Stock', dividendYield: 0,
        actions: [
          { date: '2021-03-15', type: 'buy',  qty: 150 },    // ~$23 after the SPAC pop faded
          { date: '2022-10-15', type: 'buy',  qty: 100 },    // capitulation low ~$8
          { date: '2024-12-15', type: 'sell', qty: 200 },    // trim most of the position into strength
        ],
      },
      { ticker: 'AMD',  yahoo: 'AMD',  name: 'Advanced Micro Devices', assetType: 'Stock', dividendYield: 0,
        actions: [
          { date: '2020-06-15', type: 'buy',  qty: 30 },     // ~$55
          { date: '2024-03-15', type: 'sell', qty: 'all' },  // ~$200 peak before the AI-trade pullback
        ],
      },

      // ── Small / lesser-known names where we got the timing right.
      //    These are the standout winners that beat the index. ──
      { ticker: 'SMCI', yahoo: 'SMCI', name: 'Super Micro Computer',    assetType: 'Stock', dividendYield: 0,
        actions: [
          { date: '2020-08-15', type: 'buy',  qty: 80  },    // ~$30, long before the AI server boom
          { date: '2024-02-15', type: 'sell', qty: 'all' },  // ~$1,000 at the peak — ~30× return
        ],
      },
      { ticker: 'APP',  yahoo: 'APP',  name: 'AppLovin Corp.',          assetType: 'Stock', dividendYield: 0,
        actions: [
          { date: '2022-09-15', type: 'buy',  qty: 150 },    // ~$15 after the post-IPO crash
          { date: '2024-11-15', type: 'sell', qty: 'all' },  // ~$330 — ~20× return
        ],
      },
      { ticker: 'CELH', yahoo: 'CELH', name: 'Celsius Holdings',        assetType: 'Stock', dividendYield: 0,
        actions: [
          { date: '2020-10-15', type: 'buy',  qty: 200 },    // ~$5 before the PepsiCo distribution deal
          { date: '2024-03-15', type: 'sell', qty: 'all' },  // ~$80 — ~15× return
        ],
      },

      // ── Dividend payers — trimmed and rotated out at the 2022/2023 highs ──
      { ticker: 'KO',   yahoo: 'KO',   name: 'Coca-Cola Co.',        assetType: 'Stock', startDate: '2020-05-15', sharesPerBuy: 0.40, dividendYield: 3.0,
        actions: [{ date: '2022-12-15', type: 'sell', qty: 18 }] },
      { ticker: 'JNJ',  yahoo: 'JNJ',  name: 'Johnson & Johnson',    assetType: 'Stock', startDate: '2020-05-15', sharesPerBuy: 0.18, dividendYield: 3.1,
        actions: [{ date: '2023-06-15', type: 'sell', qty: 6 }] },

      // ── Commodity (≤5%) ──
      { ticker: 'GOLD', yahoo: 'GC=F',    name: 'Gold (Spot)', assetType: 'Commodity', startDate: '2021-01-15', sharesPerBuy: 0.015 },

      // ── Crypto (≤10% combined) — DCA stops above the cutoff so we don't top-tick ──
      { ticker: 'BTC',  yahoo: 'BTC-USD', name: 'Bitcoin',     assetType: 'Crypto',    startDate: '2021-06-15', sharesPerBuy: 0.003,  dcaCutoffPrice: 90000 },
      { ticker: 'ETH',  yahoo: 'ETH-USD', name: 'Ethereum',    assetType: 'Crypto',    startDate: '2022-01-15', sharesPerBuy: 0.030,  dcaCutoffPrice: 4000  },
    ],
  },
  {
    id: 'demo-eur',
    name: 'European Growth',
    currency: 'EUR',
    // Approximate lifetime realised P/L driven by the defense breakout (RHM,
    // HAG, MTX) and a few trims on the existing positions.
    realizedApprox: 45000,
    holdings: [
      // ── Defense / aerospace breakout — the biggest source of alpha ──
      { ticker: 'RHM',  yahoo: 'RHM.DE',  name: 'Rheinmetall AG',          assetType: 'Stock', dividendYield: 1.5,
        actions: [
          { date: '2020-09-15', type: 'buy',  qty: 80 },   // ~€85 — well before the defense thesis caught on
          { date: '2021-03-15', type: 'buy',  qty: 40 },   // ~€90 add
          { date: '2024-06-15', type: 'sell', qty: 60 },   // ~€520 partial trim into the rally
          { date: '2025-03-15', type: 'sell', qty: 'all' },// ~€800 peak — full exit, lock in the win
        ],
      },
      { ticker: 'HAG',  yahoo: 'HAG.DE',  name: 'Hensoldt AG',             assetType: 'Stock', dividendYield: 1.2,
        actions: [
          { date: '2021-10-15', type: 'buy',  qty: 250 },  // ~€12 just after the IPO
          { date: '2024-04-15', type: 'sell', qty: 'all' },// ~€48 — ~4× return
        ],
      },
      { ticker: 'MTX',  yahoo: 'MTX.DE',  name: 'MTU Aero Engines',        assetType: 'Stock', dividendYield: 1.0,
        actions: [
          { date: '2020-10-15', type: 'buy',  qty: 30 },   // ~€140 post-COVID lows
          { date: '2024-12-15', type: 'sell', qty: 20 },   // ~€340 — trim 2/3 of the position
        ],
      },

      // ── Schneider Electric — energy-transition winner, steady DCA ──
      { ticker: 'SU',   yahoo: 'SU.PA',   name: 'Schneider Electric',      assetType: 'Stock', dividendYield: 2.0,
        startDate: '2020-05-15', sharesPerBuy: 0.15, dcaCutoffPrice: 220 },

      // ── ASML — biggest core conviction, trimmed at the 2024 ATH ──
      { ticker: 'ASML', yahoo: 'ASML.AS', name: 'ASML Holding',            assetType: 'Stock', dividendYield: 0.9,
        actions: [
          { date: '2020-05-15', type: 'buy',  qty: 6 },    // ~€300
          { date: '2021-03-15', type: 'buy',  qty: 4 },    // ~€500
          { date: '2024-06-15', type: 'sell', qty: 6 },    // trim near €1000 ATH
        ],
      },

      // ── Existing core European positions ──
      { ticker: 'SAP',  yahoo: 'SAP.DE',  name: 'SAP SE',                  assetType: 'Stock', startDate: '2021-06-15', sharesPerBuy: 0.55, dividendYield: 1.1, dcaCutoffPrice: 230 },
      { ticker: 'SIE',  yahoo: 'SIE.DE',  name: 'Siemens AG',              assetType: 'Stock', startDate: '2021-06-15', sharesPerBuy: 0.30, dividendYield: 2.6,
        actions: [{ date: '2024-10-15', type: 'sell', qty: 8 }] }, // trim into the €200 zone
      { ticker: 'AIR',  yahoo: 'AIR.PA',  name: 'Airbus SE',               assetType: 'Stock', startDate: '2022-01-15', sharesPerBuy: 0.45, dividendYield: 1.2 },
      { ticker: 'MC',   yahoo: 'MC.PA',   name: 'LVMH',                    assetType: 'Stock', startDate: '2020-05-15', sharesPerBuy: 0.05, dividendYield: 1.9, dcaCutoffPrice: 800,
        actions: [{ date: '2023-04-15', type: 'sell', qty: 4 }] }, // trim at the €900+ peak
      { ticker: 'OR',   yahoo: 'OR.PA',   name: "L'Oréal SA",              assetType: 'Stock', startDate: '2020-08-15', sharesPerBuy: 0.06, dividendYield: 1.5 },

      // ── Commodity (≤5%) ──
      { ticker: 'GOLD', yahoo: '4GLD.DE', name: 'Xetra-Gold',              assetType: 'Commodity', startDate: '2021-01-15', sharesPerBuy: 0.45 },

      // ── Crypto (≤10% combined) ──
      { ticker: 'BTC',  yahoo: 'BTC-EUR', name: 'Bitcoin',                 assetType: 'Crypto',    startDate: '2021-06-15', sharesPerBuy: 0.0015, dcaCutoffPrice: 85000 },
      { ticker: 'ETH',  yahoo: 'ETH-EUR', name: 'Ethereum',                assetType: 'Crypto',    startDate: '2022-01-15', sharesPerBuy: 0.020,  dcaCutoffPrice: 3500  },
    ],
  },
];

export function getDemoPortfolio(id) {
  return DEMO_PORTFOLIOS.find(p => p.id === id) || null;
}

/**
 * Month-by-month buy dates (YYYY-MM-15) from startDate up to today, inclusive.
 */
export function monthlyBuyDates(startDate) {
  const out = [];
  const start = new Date(startDate + 'T00:00:00');
  const now = new Date();
  const cursor = new Date(start.getFullYear(), start.getMonth(), 15);
  while (cursor <= now) {
    out.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-15`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return out;
}

// Rough current-price placeholders so the Portfolios list cards render
// plausible numbers without fetching live data. The detail page shows real
// Yahoo prices once the user clicks in.
const PLACEHOLDER_PRICES = {
  'BTC-USD': 70000, 'BTC-EUR': 65000,
  'ETH-USD': 3000,  'ETH-EUR': 2700,
  'GC=F': 2300,     '4GLD.DE': 75,
  'VOO': 500,       'QQQM': 200,
};

function placeholderPrice(h) {
  if (PLACEHOLDER_PRICES[h.yahoo] != null) return PLACEHOLDER_PRICES[h.yahoo];
  if (h.assetType === 'ETF')       return 400;
  if (h.assetType === 'Commodity') return 1500;
  return 150;
}

function approxQty(h) {
  let qty = 0;
  if (h.startDate && h.sharesPerBuy) {
    let months = monthlyBuyDates(h.startDate).length;
    if (h.dcaCutoffPrice) months *= 0.7; // haircut for cutoff-skipped months
    qty += months * h.sharesPerBuy;
  }
  (h.actions || []).forEach(a => {
    if (a.type === 'buy') qty += a.qty;
    else if (a.qty === 'all') qty = 0;
    else qty = Math.max(0, qty - a.qty);
  });
  return qty;
}

/**
 * Aggregate stats for the Portfolios list cards.
 *
 * When `quotes` is provided (live batch from /api/stock/quotes), uses the real
 * current price per ticker so the card matches what the detail page shows.
 * Falls back to per-asset placeholders before quotes have loaded.
 */
export function demoStats(p, quotes = null) {
  let value = 0, cost = 0, positions = 0;
  p.holdings.forEach(h => {
    const qty = approxQty(h);
    if (qty <= 0) return;
    positions++;
    const livePrice = quotes?.[h.yahoo]?.currentPrice;
    const price  = livePrice && livePrice > 0 ? livePrice : placeholderPrice(h);
    // The portfolios have been DCA'd for ~6 years across one of the strongest
    // bull markets on record — average cost ≈ current / 2.2 lines up with the
    // detail-page numbers (e.g. USD: invested ≈ value / 2.2).
    const avgBuy = price / 2.2;
    value += qty * price;
    cost  += qty * avgBuy;
  });
  const realizedPnl = p.realizedApprox ?? 0;
  const unrealized  = value - cost;
  return {
    value,
    cost,
    pnl: unrealized + realizedPnl,
    pnlPct: cost > 0 ? ((unrealized + realizedPnl) / cost) * 100 : 0,
    realizedPnl,
    positions,
  };
}
